import QuestionCard from 'components/CreateQuiz/QuestionCard';
import { PageContext } from 'pages/CreateQuiz/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Divider, IconButton, Stack, Text } from 'shell/components';

const QuestionGroup = ({ id, title, tooltip, questions, points, mobile }) => {
    const { t } = useTranslation('quiz');
    return (
        <Stack component={'section'} aria-labelledby={id} spacing={2}>
            <Stack
                direction={mobile ? 'column' : 'row'}
                alignItems={mobile ? 'flex-start' : 'center'}
                justifyContent={'space-between'}
                spacing={mobile ? 2 : 4}
            >
                <Stack direction={'row'} alignItems={'center'} spacing={2}>
                    <Text id={id} component={'h3'} color={'light'} weight={'medium'}>
                        {title}
                    </Text>
                    <IconButton
                        kind={'info'}
                        size={'xsmall'}
                        variant={'circle'}
                        aria-label={title}
                        sx={{ flexShrink: 0 }}
                        tooltip={{ component: tooltip, maxWidth: '17.5rem' }}
                    />
                </Stack>
                <Text color={'light'} weight={'medium'}>
                    {t('create.total-points', 'Total Points: {{points}}', { points })}
                </Text>
            </Stack>
            <Stack spacing={5}>
                {questions.map((question) => (
                    <QuestionCard key={question.id} question={question} mobile={mobile} />
                ))}
            </Stack>
        </Stack>
    );
};

const QuizOverview = ({ device }) => {
    const { t } = useTranslation('quiz');
    const { quiz, overview } = useContext(PageContext);
    const { title, questions, timelyQuestions, lmsQuestions, totalPoints, timelyPoints, lmsPoints } = overview;
    const isMobile = device === 'mobile';
    const lmsTooltip = t(
        'create.lms-tooltip',
        'These questions are graded in your LMS and are shown for reference only.'
    );
    const timelyTooltip = t(
        'create.timely-tooltip',
        'These written-response questions will be configured for TimelyGrader grading.'
    );
    const counts = [
        t('create.question-count', '{{count}} Questions', { count: questions.length }),
        t('create.timely-count', '{{count}} graded on TimelyGrader', { count: timelyQuestions.length }),
        t('create.lms-count', '{{count}} graded on LMS', { count: lmsQuestions.length }),
    ];

    return (
        <Container
            device={device}
            maxWidth={false}
            sx={(theme) => ({
                flex: 1,
                minHeight: 0,
                maxWidth: 'none',
                background: theme.palette.ensightful.shades[0],
                px: device === 'desktop' ? 8 : 4,
                py: 5,
            })}
        >
            <Stack spacing={5} sx={{ width: '100%' }}>
                <Stack spacing={2}>
                    <Text component={'h2'} variant={'sh1'} weight={'medium'} sx={{ fontSize: '1.25rem' }}>
                        {t('create.overview', 'Quiz Overview')}
                    </Text>
                    <Text sx={{ maxWidth: '612px' }}>
                        {t(
                            'create.description',
                            'TimelyGrader grades written-response questions. Auto-graded LMS questions, such as multiple choice or true/false, are shown for reference.'
                        )}
                    </Text>
                </Stack>
                {!quiz ? (
                    <Text role={'alert'}>
                        {t('create.missing-quiz', 'Select a quiz to import from the Quizzes page.')}
                    </Text>
                ) : (
                    <>
                        <Divider />
                        <Stack spacing={2}>
                            <Text
                                component={'h2'}
                                variant={'h6'}
                                weight={'semibold'}
                                sx={{ fontSize: '1.5rem', lineHeight: '2rem' }}
                            >
                                {title || t('create.untitled', 'Untitled Quiz')}
                            </Text>
                            <Stack
                                direction={isMobile ? 'column' : 'row'}
                                spacing={2}
                                useFlexGap
                                divider={isMobile ? undefined : <Divider orientation={'vertical'} flexItem />}
                                sx={{ flexWrap: 'wrap' }}
                            >
                                {counts.map((count, index) => (
                                    <Text key={index} color={'light'}>
                                        {count}
                                    </Text>
                                ))}
                            </Stack>
                        </Stack>
                        <Stack
                            direction={'row'}
                            justifyContent={'space-between'}
                            spacing={4}
                            sx={(theme) => ({
                                p: 5,
                                border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                                borderRadius: '16px',
                            })}
                        >
                            <Text variant={'p1'} weight={'medium'}>
                                {t('create.total-score', 'Total Score')}
                            </Text>
                            <Text variant={'p1'} weight={'medium'}>
                                {t('create.points', 'Points: {{points}}', { points: totalPoints })}
                            </Text>
                        </Stack>
                        {questions.length === 0 ? (
                            <Text>{t('create.no-questions', 'This quiz has no questions to display.')}</Text>
                        ) : (
                            <>
                                <QuestionGroup
                                    id={'quiz-subjective-heading'}
                                    title={t('create.subjective', 'Subjective Questions')}
                                    tooltip={timelyTooltip}
                                    questions={timelyQuestions}
                                    points={timelyPoints}
                                    mobile={isMobile}
                                />
                                {timelyQuestions.length === 0 && (
                                    <Text>
                                        {t(
                                            'create.no-written-questions',
                                            'This quiz has no written-response questions for TimelyGrader grading.'
                                        )}
                                    </Text>
                                )}
                                <QuestionGroup
                                    id={'quiz-lms-heading'}
                                    title={t('create.auto-graded', 'Auto-Graded Questions')}
                                    tooltip={lmsTooltip}
                                    questions={lmsQuestions}
                                    points={lmsPoints}
                                    mobile={isMobile}
                                />
                            </>
                        )}
                    </>
                )}
            </Stack>
        </Container>
    );
};

export default QuizOverview;
